import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	Alert,
	Button,
	Checkbox,
	DatePicker,
	Divider,
	Form,
	Input,
	Modal,
	Select,
	Space,
	Switch,
	Tag,
	Typography,
} from 'antd';
import { getSessionUser } from "../../../utils/auth/sessionUser";
import dayjs from 'dayjs';
import { DeleteOutlined, FileSyncOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { createPatient, createPatientOtherName } from '../../../api/healthcare/PatientApi';
import { assignPlan, fetchTemplates } from '../../../api/healthcare/TreatmentPlanApi';
import { TreatmentPlanTemplate } from '../../../types/healthcare/TreatmentPlanTypes';
import { Patient } from '../../../types/healthcare/PatientTypes';
import {
	PatientOtherNameDraft,
	PatientOtherNameSubmission,
} from '../../../types/healthcare/PatientNameTypes';
import PatientOtherNameFields from './patientNames/PatientOtherNameFields';
import {
	FAMILY_NAME_LABEL,
	FAMILY_NAME_REQUIRED_MESSAGE,
	GIVEN_NAME_LABEL,
	GIVEN_NAME_REQUIRED_MESSAGE,
	MIDDLE_NAME_LABEL,
	ONE_NAME_ONLY_LABEL,
	patientNameContextLabel,
	patientNameRoleParts,
	validatePatientOtherName,
} from './patientNames/patientNameDisplay';
import {
	extractIhiValidationFromMutationError,
	parseIhiValidationFeedback,
} from '../../../utils/healthcare/IhiValidationFeedback';
import { validateIHI } from '../../../utils/healthcare/ValidateIhiNumber';
import {
	DVA_FILE_NUMBER_TOO_LONG_MESSAGE,
	dvaFileNumberWithinLimit,
	toCanonicalDva,
} from '../../../utils/healthcare/DvaFileNumber';
import { useNotifications } from '../../../context/NotificationContext';
import MedicalDirectorImportModal from './MedicalDirectorImportModal';
import { XmlImportJobStatus } from '../../../api/healthcare/PatientApi';
import { generatePassword } from '../../../utils/generatePassword';
import { sendCommunication } from '../../../api/communications/communicationApi';
import { SendCommunicationRequest } from '../../../types/communications/CommunicationTypes';
import { ENV } from '../../../config/runtimeEnv';
import type { UserUuid } from '../../../types/identity';

const { Text } = Typography;

interface Props {
	open: boolean;
	onClose: () => void;
	onCreated: (patient: Patient) => void;
	/**
	 * B1.4-E: hand the operator to the preserved patient record. Invoked when
	 * post-create name recovery finishes, and when the operator explicitly
	 * abandons the remaining local drafts.
	 */
	onReviewCreatedPatient?: (patient: Patient) => void;
}

const GENDER_OPTIONS = [
	{ value: 'Male', label: 'Male' },
	{ value: 'Female', label: 'Female' },
];

// B1.4-E — fixed, value-free operator messages. No patient-name component and no
// identifier is ever interpolated into any of them.
export const PATIENT_CREATED_MESSAGE = 'Patient created successfully.';
export const PATIENT_CREATED_NAMES_INCOMPLETE_TITLE =
	'Patient created — additional names incomplete';
export const PATIENT_CREATED_NAMES_INCOMPLETE_MESSAGE =
	'The patient record was created and has been kept. One or more additional names were not recorded. ' +
	'The remaining names are still listed below — correct them if needed and record them again.';
export const PATIENT_RECOVERY_COMPLETED_MESSAGE = 'The remaining additional names were recorded.';
export const PATIENT_RECOVERY_DISCARDED_MESSAGE =
	'The unrecorded additional names were discarded. The patient record and the names already recorded have been kept.';
export const PATIENT_RECOVERY_ABANDON_TITLE = 'Discard the remaining names?';
export const PATIENT_RECOVERY_ABANDON_BODY =
	'Only the additional names that have not been recorded yet will be discarded. The patient record and the names already recorded are kept.';

// P3 (86d3yf9qr) — this modal used to create a credentialed account and tell
// nobody. The generated password exists in local scope only and is not stored or
// re-derivable, so the welcome notification is the ONE place it is ever readable.
// The operator can opt out, but the default is to send.
export const LOGIN_DETAILS_SEND_LABEL = 'Send login details to the patient';
export const LOGIN_DETAILS_NOT_SENT_TITLE = 'Login details not sent';
// Value-free, like every other message in this modal: no name, no identifier and
// — critically — no credential is interpolated into operator-facing text.
export const LOGIN_DETAILS_NOT_SENT_MESSAGE =
	'The patient record was created, but the login details could not be sent. ' +
	'Resend them from the patient record, or ask the patient to use the reset-password link on the login page.';

// CU-86d42vjxm — fixed titles for the bounded manual-IHI outcome. The body text
// is always the server's own bounded message, never response or provider text.
export const IHI_NOT_SAVED_TITLE = 'IHI not saved';
export const IHI_SAVED_TITLE = 'IHI saved';
export const PATIENT_NOT_SAVED_TITLE = 'Patient not saved';
// CU-86d42vjxm — used when an IHI attempt is rejected with metadata the bounded
// parser refuses, so no server decision about the candidate may be claimed.
export const PATIENT_NOT_SAVED_UNCONFIRMED_MESSAGE =
	'The patient record was not saved, and the outcome of the supplied IHI could not be confirmed. ' +
	'Check the entered details and try again, or create the patient without an IHI.';

// CU-86d42vjxm — the field is optional, so only a supplied candidate is checked,
// through the existing validateIHI contract rather than a second local policy.
const validateIhiCandidate = (_rule: unknown, value: unknown) => {
	if (typeof value !== 'string' || value === '') return Promise.resolve();
	const result = validateIHI(value);
	return result.isValid
		? Promise.resolve()
		: Promise.reject(new Error(result.error || 'Invalid IHI number.'));
};

// CU-14ygtkj8z3j — the only client rule is the shared 32-code-point limit; no format or checksum rule exists for a DVA file number.
const validateDvaFileNumberCandidate = (_rule: unknown, value: unknown) => {
	if (typeof value !== 'string' || dvaFileNumberWithinLimit(value)) return Promise.resolve();
	return Promise.reject(new Error(DVA_FILE_NUMBER_TOO_LONG_MESSAGE));
};

const EMPTY_OTHER_NAME: PatientOtherNameSubmission = {
	family_name: '',
	given_name: null,
	middle_name: null,
	is_one_name_only: false,
	name_context: 'general',
};

/**
 * B1.4-E — authenticated new-patient capture.
 *
 * The canonical primary name is captured with explicit given / middle / family
 * roles plus an explicit one-name-only state (HI_005_005804 / HI_010_005804 /
 * HI_015_005804). Additional OTHER names may be collected locally before a
 * patient id exists (HI_010_005809); they are recorded through the authenticated
 * nested other-name route only AFTER the canonical patient has been created.
 *
 * There is no batch or nested-write contract: each collected name is one ordinary
 * authenticated request against the created patient.
 *
 * PR #199 review correction — post-create recovery. Closing on the first
 * other-name failure destroyed the failed draft and every later unattempted
 * draft, and the opened patient record could not recover them because it can only
 * read names the server already stored. The modal now switches into an explicit
 * recovery mode instead:
 *
 *  - the canonical patient is created exactly once and held in local recovery
 *    state, so a retry can never create a second patient;
 *  - drafts are submitted sequentially; on failure the failed draft and every
 *    later unattempted draft are retained, while already-recorded drafts are
 *    dropped and never resubmitted;
 *  - the retained drafts stay visible and editable, and submitting retries only
 *    them against the existing patient id;
 *  - abandoning recovery requires an explicit confirmation and discards only the
 *    unrecorded local drafts.
 *
 * Recovery state lives in React state only. No patient-name value is written to
 * localStorage, sessionStorage, a URL, a log or a notification.
 */
const QuickCreatePatientModal: React.FC<Props> = ({
	open,
	onClose,
	onCreated,
	onReviewCreatedPatient,
}) => {
	const [form] = Form.useForm();
	const [submitting, setSubmitting] = useState(false);
	const [templates, setTemplates] = useState<TreatmentPlanTemplate[]>([]);
	const [loadingTemplates, setLoadingTemplates] = useState(false);
	const [importModalOpen, setImportModalOpen] = useState(false);
	const { showError, showSuccess, showWarning } = useNotifications();

	// Explicit one-name-only state for the canonical primary name. Held locally so
	// the given/middle inputs can be cleared and disabled while it is set.
	const [oneNameOnly, setOneNameOnly] = useState(false);

	// P3 (86d3yf9qr): defaults ON — a credentialed account nobody can log into is
	// the defect this closes. The operator can still opt out per patient.
	const [sendLoginDetails, setSendLoginDetails] = useState(true);

	// Other names collected locally before a patient id exists. A draft is not a
	// recorded name: removing one discards local input and retires nothing, so the
	// no-hard-delete rule for recorded names is not weakened here.
	const [otherNameDrafts, setOtherNameDrafts] = useState<PatientOtherNameDraft[]>([]);
	const [draftEditorIndex, setDraftEditorIndex] = useState<number | 'new' | null>(null);
	const [draftEditorValue, setDraftEditorValue] =
		useState<PatientOtherNameSubmission>(EMPTY_OTHER_NAME);
	const [draftEditorErrors, setDraftEditorErrors] = useState<string[]>([]);

	/**
	 * Post-create recovery state. Non-null means the canonical patient exists and
	 * only the retained drafts remain outstanding — createPatient must not run
	 * again while this is set.
	 */
	const [createdPatient, setCreatedPatient] = useState<Patient | null>(null);
	const [abandonConfirmOpen, setAbandonConfirmOpen] = useState(false);
	const isRecoveryMode = createdPatient !== null;

	// Monotonic list-key source. Deriving a key from the list length would collide
	// after a removal and make React reuse the wrong row's state.
	const draftKeyCounter = useRef(0);

	const user = getSessionUser();

	const loadTemplates = useCallback(async () => {
		setLoadingTemplates(true);
		try {
			const data = await fetchTemplates();
			setTemplates(data);
		} catch {
			// non-critical — just won't show templates
		} finally {
			setLoadingTemplates(false);
		}
	}, []);

	const resetAllState = () => {
		form.resetFields();
		setOneNameOnly(false);
		setSendLoginDetails(true);
		setOtherNameDrafts([]);
		setDraftEditorIndex(null);
		setDraftEditorValue(EMPTY_OTHER_NAME);
		setDraftEditorErrors([]);
		setCreatedPatient(null);
		setAbandonConfirmOpen(false);
	};

	useEffect(() => {
		if (open) {
			resetAllState();
			loadTemplates();
		}
	}, [open]);

	const handleOneNameOnly = (checked: boolean) => {
		setOneNameOnly(checked);
		if (checked) {
			// A one-name-only record has no given or middle component at all; the
			// single name is captured in the family-name role.
			form.setFieldsValue({ first_name: '', middle_name: '' });
		}
	};

	const closeDraftEditor = () => {
		setDraftEditorIndex(null);
		setDraftEditorValue(EMPTY_OTHER_NAME);
		setDraftEditorErrors([]);
	};

	const saveDraftEditor = () => {
		const errors = validatePatientOtherName(draftEditorValue);
		if (errors.length > 0) {
			setDraftEditorErrors(errors);
			return;
		}

		setOtherNameDrafts((prev) => {
			if (draftEditorIndex === 'new') {
				draftKeyCounter.current += 1;
				return [...prev, { ...draftEditorValue, draft_key: `draft-${draftKeyCounter.current}` }];
			}
			return prev.map((draft, index) =>
				index === draftEditorIndex ? { ...draft, ...draftEditorValue } : draft
			);
		});
		closeDraftEditor();
	};

	/**
	 * Record each draft in order against an existing patient id, and return the
	 * drafts that were NOT recorded.
	 *
	 * On the first failure the failed draft and every later unattempted draft are
	 * returned so they can be retained for retry. Drafts already recorded are
	 * excluded from the remainder and are therefore never resubmitted. No response
	 * content is read or logged.
	 */
	const recordDrafts = async (
		patientId: UserUuid,
		drafts: PatientOtherNameDraft[]
	): Promise<PatientOtherNameDraft[]> => {
		for (let index = 0; index < drafts.length; index += 1) {
			try {
				await createPatientOtherName(patientId, drafts[index]);
			} catch {
				return drafts.slice(index);
			}
		}
		return [];
	};

	/**
	 * P3 (86d3yf9qr) — hand the generated credential to the patient.
	 *
	 * Same payload shape and same `VITE_NOTIFICATIONS_ENABLED` gate as
	 * `PatientModal.updatePatientInfo()`; no second pattern. `id` (the template
	 * override) is deliberately omitted — this modal offers no template picker, so
	 * there is nothing to pass and the notification service resolves the clinic's
	 * active `new_patient_notification` template itself.
	 *
	 * ⚠️ The password comes from the LOCAL variable, never from the create
	 * response: the authentication service's User model hides `password` and has
	 * no `password_confirmation` column, so reading it back yields undefined and
	 * the template's `{{password}}` placeholder is delivered verbatim (the P1 bug).
	 *
	 * Not awaited, and its rejection is handled here rather than left to become an
	 * unhandled promise rejection. A clinic with no active `new_patient_notification`
	 * template answers 404 — the patient is still created, and the operator is told
	 * to resend rather than shown a failed create.
	 */
	const sendLoginDetailsNotification = (created: Patient, password: string) => {
		if (!sendLoginDetails) return;
		if (ENV.VITE_NOTIFICATIONS_ENABLED !== 'true') return;

		const notificationPayload: SendCommunicationRequest = {
			recipient_user_id: created.id,
			category: 'new_patient_notification',
			user_id: user.id,
			channels: ['email', 'sms'],
			data: { ...created, password },
		};

		sendCommunication(notificationPayload).catch(() => {
			// No response body, status or credential is carried into the message.
			showWarning(LOGIN_DETAILS_NOT_SENT_TITLE, LOGIN_DETAILS_NOT_SENT_MESSAGE);
		});
	};

	/** Finish the workflow: hand off the preserved record, reset and close. */
	const completeAndClose = (patient: Patient, openRecord: boolean) => {
		onCreated(patient);
		if (openRecord) onReviewCreatedPatient?.(patient);
		resetAllState();
		onClose();
	};

	/** First submission: create the canonical patient, then record the drafts. */
	const handleCreateSubmit = async () => {
		let values: any;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}

		setSubmitting(true);
		const password = generatePassword();
		const ihiCandidate = typeof values.ihi_number === 'string' ? values.ihi_number : '';
		const dvaCandidate = toCanonicalDva(values.dva_file_number);

		try {
			const patientData: Partial<Patient> = {
				first_name:            oneNameOnly ? '' : values.first_name,
				middle_name:           oneNameOnly ? '' : values.middle_name ?? '',
				last_name:             values.last_name,
				has_one_name_only:     oneNameOnly,
				email:                 values.email,
				phone:                 values.phone ?? '',
				date_of_birth:         values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : null,
				gender:                values.gender ?? '',
				// CU-14ygtkj8z3j — Sex is the HI-service search criterion and is never derived from Gender.
				sex:                   values.sex ?? '',
				is_active:             true,
				password,
				password_confirmation: password,
				address:               '',
				city:                  '',
				state:                 '',
				country:               '',
				postal_code:           '',
				profile_picture:       '',
				preferences:           '',
				panel_key:             '',
				reagents:              '',
				height:                '',
				weight:                '',
				roles:                 [],
				username:              values.email,
			};

			// CU-86d42vjxm — a supplied candidate travels only in the supported nested
			// patient-information location, and only when the operator entered one; an
			// untouched field stays omitted rather than becoming an explicit clear.
			if (ihiCandidate !== '') {
				patientData.patient_info = { ihi_number: Number(ihiCandidate) };
			}

			// CU-14ygtkj8z3j — merged, never assigned, so a DVA file number and a manual IHI coexist in patient_info.
			if (dvaCandidate !== null) {
				patientData.patient_info = { ...patientData.patient_info, dva_file_number: dvaCandidate };
			}

			// The canonical patient is created FIRST, and exactly once per modal
			// session. Only once it exists can the collected other names be recorded
			// against its id.
			// CU-86d42vjxm — the bounded ihi_validation block is response metadata: it
			// is destructured off so it can never become patient state, and it is read
			// only through the existing fail-closed parser.
			const { ihi_validation, ...created } = await createPatient(patientData);
			const ihiValidation = parseIhiValidationFeedback(ihi_validation);

			if (ihiValidation) {
				const serverIhi = created.patient_info?.ihi_number;
				form.setFieldsValue({
					ihi_number: serverIhi === undefined || serverIhi === null ? '' : String(serverIhi),
				});
				if (ihiValidation.candidate_discarded) {
					showWarning(IHI_NOT_SAVED_TITLE, ihiValidation.message);
				}
			}

			// P3: dispatched as soon as the account exists, so a later treatment-plan
			// or other-name failure cannot leave the patient without their credentials.
			sendLoginDetailsNotification(created, password);

			// Tied to creation, so it runs once and is unaffected by name recovery.
			if (values.treatment_plan_template_id && created.id) {
				try {
					await assignPlan({
						patient_id:                  created.id,
						treatment_plan_template_id:  values.treatment_plan_template_id,
						practitioner_id:             user.id,
					});
				} catch {
					showError('Patient created but treatment plan could not be assigned — assign it manually from the patient record.');
				}
			}

			const remaining = created?.id
				? await recordDrafts(created.id, otherNameDrafts)
				: otherNameDrafts;

			if (remaining.length === 0) {
				if (ihiValidation && !ihiValidation.candidate_discarded) {
					showSuccess(PATIENT_CREATED_MESSAGE, ihiValidation.message);
				} else {
					showSuccess(PATIENT_CREATED_MESSAGE);
				}
				completeAndClose(created, false);
				return;
			}

			// Partial completion: keep the created patient and the outstanding drafts,
			// warn once with fixed text, and stay open in recovery mode. The list is
			// refreshed so the preserved record is already visible behind the modal.
			setCreatedPatient(created);
			setOtherNameDrafts(remaining);
			onCreated(created);
			// CU-86d42vjxm — the create completed, so its verified outcome is surfaced
			// here rather than being lost with the skipped success branch.
			if (ihiValidation && !ihiValidation.candidate_discarded) {
				showSuccess(IHI_SAVED_TITLE, ihiValidation.message);
			}
			showWarning(
				PATIENT_CREATED_NAMES_INCOMPLETE_TITLE,
				PATIENT_CREATED_NAMES_INCOMPLETE_MESSAGE
			);
		} catch (err: any) {
			// CU-86d42vjxm — a bounded refusal is surfaced from the fixed contract alone.
			const refusedIhiValidation = extractIhiValidationFromMutationError(err);
			if (refusedIhiValidation?.candidate_discarded) {
				form.setFieldsValue({ ihi_number: '' });
				showWarning(PATIENT_NOT_SAVED_TITLE, refusedIhiValidation.message);
				return;
			}

			// CU-86d42vjxm — the rejection of an IHI attempt may carry candidate or
			// provider detail in its response text, so untrusted metadata renders the
			// fixed message instead.
			if (ihiCandidate !== '' && err?.response?.status === 422) {
				showWarning(PATIENT_NOT_SAVED_TITLE, PATIENT_NOT_SAVED_UNCONFIRMED_MESSAGE);
				return;
			}

			const msg =
				err?.response?.data?.message ||
				err?.response?.data?.error ||
				'Failed to create patient';
			showError(msg);
		} finally {
			setSubmitting(false);
		}
	};

	/** Recovery submission: retry only the retained drafts. Never creates a patient. */
	const handleRecoverySubmit = async (patient: Patient) => {
		setSubmitting(true);
		try {
			const remaining = await recordDrafts(patient.id, otherNameDrafts);

			if (remaining.length === 0) {
				showSuccess(PATIENT_RECOVERY_COMPLETED_MESSAGE);
				completeAndClose(patient, true);
				return;
			}

			setOtherNameDrafts(remaining);
			showWarning(
				PATIENT_CREATED_NAMES_INCOMPLETE_TITLE,
				PATIENT_CREATED_NAMES_INCOMPLETE_MESSAGE
			);
		} finally {
			setSubmitting(false);
		}
	};

	const handleSubmit = () => {
		if (createdPatient) return handleRecoverySubmit(createdPatient);
		return handleCreateSubmit();
	};

	/**
	 * Closing during recovery would silently drop the unrecorded drafts, so it
	 * requires an explicit confirmation first.
	 */
	const handleRequestClose = () => {
		if (createdPatient && otherNameDrafts.length > 0) {
			setAbandonConfirmOpen(true);
			return;
		}
		if (createdPatient) {
			completeAndClose(createdPatient, false);
			return;
		}
		onClose();
	};

	const handleConfirmAbandon = () => {
		const patient = createdPatient;
		setAbandonConfirmOpen(false);
		if (!patient) return;
		// Only the local drafts are discarded. The created patient and every name
		// already recorded on the server remain untouched.
		showWarning(PATIENT_RECOVERY_ABANDON_TITLE, PATIENT_RECOVERY_DISCARDED_MESSAGE);
		completeAndClose(patient, true);
	};

	const handleImportComplete = (_job: XmlImportJobStatus) => {
		setImportModalOpen(false);
		onClose();
	};

	const draftList =
		otherNameDrafts.length === 0 ? (
			<Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
				No other names added.
			</Text>
		) : (
			otherNameDrafts.map((draft, index) => (
				<div
					key={draft.draft_key}
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 8,
						flexWrap: 'wrap',
						padding: '4px 0',
						borderTop: '1px solid var(--mg-border)',
					}}
				>
					<Space size={[8, 4]} wrap>
						{patientNameRoleParts(draft).map((part) => (
							<span key={part.label} style={{ fontSize: 12 }}>
								<Text type="secondary" style={{ fontSize: 11 }}>{part.label}: </Text>
								<Text style={{ fontSize: 12 }}>{part.value}</Text>
							</span>
						))}
						{draft.is_one_name_only && <Tag>{ONE_NAME_ONLY_LABEL}</Tag>}
						<span style={{ fontSize: 12 }}>
							<Text type="secondary" style={{ fontSize: 11 }}>Context: </Text>
							<Text style={{ fontSize: 12 }}>{patientNameContextLabel(draft.name_context)}</Text>
						</span>
					</Space>
					<Space size={6}>
						<Button
							size="small"
							onClick={() => {
								setDraftEditorValue({
									family_name: draft.family_name,
									given_name: draft.given_name,
									middle_name: draft.middle_name,
									is_one_name_only: draft.is_one_name_only,
									name_context: draft.name_context,
								});
								setDraftEditorErrors([]);
								setDraftEditorIndex(index);
							}}
						>
							Edit
						</Button>
						<Button
							size="small"
							icon={<DeleteOutlined />}
							aria-label="Remove other name"
							onClick={() => setOtherNameDrafts((prev) => prev.filter((_, i) => i !== index))}
						/>
					</Space>
				</div>
			))
		);

	const addDraftButton = (
		<Button
			type="dashed"
			size="small"
			icon={<PlusOutlined />}
			style={{ marginTop: 8 }}
			onClick={() => {
				setDraftEditorValue(EMPTY_OTHER_NAME);
				setDraftEditorErrors([]);
				setDraftEditorIndex('new');
			}}
		>
			Add other name
		</Button>
	);

	return (
		<>
		<Modal
			open={open}
			title={
				<Space>
					<UserAddOutlined style={{ color: '#7c3aed' }} />
					<span>{isRecoveryMode ? 'Additional Names Outstanding' : 'New Patient'}</span>
				</Space>
			}
			footer={
				<Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
					{isRecoveryMode ? (
						<span />
					) : (
						<Button icon={<FileSyncOutlined />} onClick={() => setImportModalOpen(true)}>
							Import Patient Record
						</Button>
					)}
					<Space>
						<Button onClick={handleRequestClose}>
							{isRecoveryMode ? 'Discard remaining names' : 'Cancel'}
						</Button>
						<Button
							type="primary"
							loading={submitting}
							onClick={handleSubmit}
							data-testid="patient-form-submit"
						>
							{isRecoveryMode ? 'Record remaining names' : 'Create Patient'}
						</Button>
					</Space>
				</Space>
			}
			onCancel={handleRequestClose}
			width={560}
			destroyOnClose
		>
			{/*
			 * The Form element stays mounted in recovery mode so the useForm instance
			 * remains connected — resetting fields on an unmounted Form is a no-op
			 * that antd warns about. The demographic fields are simply not rendered;
			 * antd preserves their values in the store.
			 */}
			<Form form={form} layout="vertical" size="middle" style={{ marginTop: 8 }}>
			{isRecoveryMode ? (
				<div>
					<Alert
						type="warning"
						showIcon
						style={{ marginBottom: 12 }}
						message={PATIENT_CREATED_NAMES_INCOMPLETE_TITLE}
						description={PATIENT_CREATED_NAMES_INCOMPLETE_MESSAGE}
					/>
					<Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
						These names have not been recorded yet. Correct any of them and record them again,
						or discard the ones you no longer want.
					</Text>
					{draftList}
					{addDraftButton}
				</div>
			) : (
			<>
				{/* ── Primary name ───────────────────────────────────────── */}
				<Form.Item label={ONE_NAME_ONLY_LABEL} style={{ marginBottom: 12 }}>
					<Switch
						aria-label={ONE_NAME_ONLY_LABEL}
						checked={oneNameOnly}
						onChange={handleOneNameOnly}
					/>
				</Form.Item>

				<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
					<Form.Item
						name="first_name"
						label={GIVEN_NAME_LABEL}
						rules={oneNameOnly ? [] : [{ required: true, message: GIVEN_NAME_REQUIRED_MESSAGE }]}
						style={{ flex: 1, minWidth: 160, marginBottom: 12 }}
					>
						<Input
							disabled={oneNameOnly}
							autoFocus
							data-testid="patient-form-firstname"
						/>
					</Form.Item>
					<Form.Item
						name="middle_name"
						label={MIDDLE_NAME_LABEL}
						style={{ flex: 1, minWidth: 160, marginBottom: 12 }}
					>
						<Input disabled={oneNameOnly} />
					</Form.Item>
					<Form.Item
						name="last_name"
						label={FAMILY_NAME_LABEL}
						rules={[{ required: true, message: FAMILY_NAME_REQUIRED_MESSAGE }]}
						style={{ flex: 1, minWidth: 160, marginBottom: 12 }}
					>
						<Input data-testid="patient-form-lastname" />
					</Form.Item>
				</div>

				<Form.Item
					name="email"
					label="Email"
					rules={[
						{ required: true, message: 'Required' },
						{ type: 'email', message: 'Enter a valid email' },
					]}
					style={{ marginBottom: 12 }}
				>
					<Input placeholder="jane.smith@example.com" data-testid="patient-form-email" />
				</Form.Item>

				<Form.Item name="phone" label="Phone" style={{ marginBottom: 12 }}>
					<Input placeholder="+61 4XX XXX XXX" />
				</Form.Item>

				<div style={{ display: 'flex', gap: 12 }}>
					<Form.Item
						name="date_of_birth"
						label="Date of Birth"
						style={{ flex: 1, marginBottom: 12 }}
						help="Select a full date including day, month and four-digit year."
					>
						<DatePicker
							style={{ width: '100%' }}
							format="DD/MM/YYYY"
							disabledDate={(d) => d && d.isAfter(dayjs())}
							placeholder="DD/MM/YYYY"
							inputReadOnly
						/>
					</Form.Item>
					<Form.Item name="gender" label="Gender" style={{ flex: 1, marginBottom: 12 }}>
						<Select options={GENDER_OPTIONS} placeholder="Select…" allowClear />
					</Form.Item>
				</div>

				{/* CU-14ygtkj8z3j — Sex is a separate HI-service criterion; same bounded free-text control as ProfileTab and RegisterKit. */}
				<Form.Item name="sex" label="Sex" style={{ width: 'calc(50% - 6px)', marginBottom: 12 }}>
					<Input maxLength={10} placeholder="Male / Female" data-testid="patient-form-sex" />
				</Form.Item>

				{/* ── Healthcare identifier (optional) ───────────────────── */}
				{/* HI_005_005802 / HI_010_005802 / HI_015_005802: manual IHI entry is
				    permitted wherever a patient demographic record is captured. This is
				    the UC.005 capture / UC.010 registration surface; BillingTab is the
				    UC.015 update surface. The field is deliberately optional — an absent
				    IHI never blocks a patient save (App\Rules\IhiNumber passes null).
				    validateIhiCandidate enforces HI_010_005801 (sixteen continuous digits
				    + Luhn) on a value that IS supplied. */}
				<Divider style={{ margin: '4px 0 12px' }}>
					<Text type="secondary" style={{ fontSize: 11 }}>Healthcare Identifier (optional)</Text>
				</Divider>

				<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
					<Form.Item
						name="ihi_number"
						label="IHI Number"
						rules={[{ validator: validateIhiCandidate }]}
						style={{ flex: 1, minWidth: 200, marginBottom: 12 }}
					>
						<Input
							inputMode="numeric"
							placeholder="Enter IHI number"
							data-testid="patient-form-ihi"
						/>
					</Form.Item>
					{/* CU-14ygtkj8z3j — optional DVA file number for the UC.010 registration search; the backend decides eligibility. */}
					<Form.Item
						name="dva_file_number"
						label="DVA File Number"
						rules={[{ validator: validateDvaFileNumberCandidate }]}
						style={{ flex: 1, minWidth: 200, marginBottom: 12 }}
					>
						<Input placeholder="Enter DVA file number" data-testid="patient-form-dva" />
					</Form.Item>
				</div>

				{/* ── Other names (collected locally, recorded after creation) ── */}
				<Divider style={{ margin: '4px 0 12px' }}>
					<Text type="secondary" style={{ fontSize: 11 }}>Other Names (optional)</Text>
				</Divider>

				<Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
					Other names are additional names the patient is also known by. They are not previous
					names, and they are recorded after the patient record is created.
				</Text>

				{draftList}
				{addDraftButton}

				<Divider style={{ margin: '12px 0' }}>
					<Text type="secondary" style={{ fontSize: 11 }}>Treatment Plan (optional)</Text>
				</Divider>

				<Form.Item name="treatment_plan_template_id" label="Assign Treatment Plan" style={{ marginBottom: 8 }}>
					<Select
						placeholder="Select a template…"
						allowClear
						loading={loadingTemplates}
						showSearch
						optionFilterProp="label"
						options={templates.map((t) => ({ value: t.id, label: t.name }))}
						notFoundContent={
							loadingTemplates ? 'Loading…' : templates.length === 0 ? 'No templates available' : undefined
						}
						optionRender={(opt) => {
							const tpl = templates.find((t) => t.id === opt.value);
							return (
								<Space direction="vertical" size={2} style={{ padding: '2px 0' }}>
									<Text style={{ fontSize: 13 }}>{opt.label}</Text>
									{tpl?.description && (
										<Text type="secondary" style={{ fontSize: 11 }}>{tpl.description}</Text>
									)}
								</Space>
							);
						}}
					/>
				</Form.Item>

				{/* ── Login details (P3) ─────────────────────────────────── */}
				<Divider style={{ margin: '12px 0' }}>
					<Text type="secondary" style={{ fontSize: 11 }}>Login Details</Text>
				</Divider>

				<Form.Item style={{ marginBottom: 8 }}>
					<Checkbox
						checked={sendLoginDetails}
						onChange={(e) => setSendLoginDetails(e.target.checked)}
						data-testid="patient-form-send-login-details"
					>
						{LOGIN_DETAILS_SEND_LABEL}
					</Checkbox>
				</Form.Item>

				<Alert
					type="info"
					showIcon={false}
					message={
						<Text type="secondary" style={{ fontSize: 11 }}>
							{sendLoginDetails
								? 'A temporary password will be auto-generated and sent to the patient with their login details. The patient can reset it via the login page.'
								: 'A temporary password will be auto-generated but nothing will be sent. The patient will need the reset-password link on the login page before they can sign in.'}
						</Text>
					}
					style={{ padding: '6px 10px' }}
				/>
			</>
			)}
			</Form>
		</Modal>

		<Modal
			open={draftEditorIndex !== null}
			title={draftEditorIndex === 'new' ? 'Add other name' : 'Edit other name'}
			okText="Save"
			onOk={saveDraftEditor}
			onCancel={closeDraftEditor}
			destroyOnClose
		>
			{draftEditorErrors.length > 0 && (
				<Alert
					type="error"
					showIcon
					style={{ marginBottom: 12 }}
					message={draftEditorErrors.join(' ')}
				/>
			)}
			<PatientOtherNameFields
				idPrefix="new-patient-other-name"
				value={draftEditorValue}
				onChange={setDraftEditorValue}
			/>
		</Modal>

		<Modal
			open={abandonConfirmOpen}
			title={PATIENT_RECOVERY_ABANDON_TITLE}
			okText="Discard"
			cancelText="Keep editing"
			onOk={handleConfirmAbandon}
			onCancel={() => setAbandonConfirmOpen(false)}
		>
			<Text>{PATIENT_RECOVERY_ABANDON_BODY}</Text>
		</Modal>

		<MedicalDirectorImportModal
			open={importModalOpen}
			onClose={() => setImportModalOpen(false)}
			mode="new_patient"
			onImportComplete={handleImportComplete}
		/>
		</>
	);
};

export default QuickCreatePatientModal;
